"use client";

import React, { useCallback, useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "./contexts/AuthContext";
import { temPermissao } from "./utils/permissoes";
import { ENDPOINTS } from "./endpoints";
import { ordenarPorTexto } from "./utils/ordenar";
import { Loader } from "./components/Loader";

export type SecaoPreventiva = "agenda" | "ativos" | "tipos" | "planos";

type Tipo = { id: number; descricao: string; ativo: number };
type Departamento = { id: number; descricao: string };
type Ativo = {
  id: number;
  tipo_id: number;
  departamento_id: number | null;
  codigo: string | null;
  descricao: string;
  local_descricao: string | null;
  marca: string | null;
  modelo: string | null;
  observacoes: string | null;
  ativo: number;
  tipo_descricao?: string;
  departamento_descricao?: string;
};
type Plano = {
  id: number;
  ativo_id: number;
  descricao: string;
  intervalo_dias: number;
  dth_ultima: string | null;
  dth_proxima: string | null;
  ativo: number;
  ativo_descricao?: string;
  local_descricao?: string;
  tipo_descricao?: string;
  situacao?: string;
  os_aberta_id?: number | null;
};
type Agenda = {
  atrasadas: Plano[];
  a_vencer: Plano[];
  em_dia: Plano[];
  sem_data: Plano[];
  total: number;
};

function badgeSituacao(situacao?: string) {
  switch (situacao) {
    case "ATRASADA":
      return "bg-red-600 text-white";
    case "A_VENCER":
      return "bg-yellow-400 text-gray-900";
    case "EM_DIA":
      return "bg-green-500 text-white";
    default:
      return "bg-gray-300 text-gray-800";
  }
}

function labelSituacao(situacao?: string) {
  switch (situacao) {
    case "ATRASADA":
      return "Atrasada";
    case "A_VENCER":
      return "A vencer";
    case "EM_DIA":
      return "Em dia";
    case "SEM_DATA":
      return "Sem data";
    default:
      return situacao || "-";
  }
}

const titulos: Record<SecaoPreventiva, string> = {
  agenda: "Agenda de Preventivas",
  ativos: "Ativos",
  tipos: "Tipos de Ativo",
  planos: "Planos",
};

export default function Preventivas({
  secao,
  visivel = true,
}: {
  secao: SecaoPreventiva;
  visivel?: boolean;
}) {
  const { loginUnidade, permissoes, loginGrupoNome, loginLoginId, loginLoginNome } = useAuth();
  const unidade = (loginUnidade || "").toLowerCase();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; type?: "error" | "success" }>({
    open: false,
    message: "",
    type: "error",
  });

  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);

  const [modalTipo, setModalTipo] = useState(false);
  const [modalAtivo, setModalAtivo] = useState(false);
  const [modalPlano, setModalPlano] = useState(false);
  const [confirmaExcluir, setConfirmaExcluir] = useState<{
    tipo: "tipo" | "ativo" | "plano";
    id: number;
    label: string;
  } | null>(null);

  const [formTipo, setFormTipo] = useState<{ id?: number; descricao: string; ativo: number }>({
    descricao: "",
    ativo: 1,
  });
  const [formAtivo, setFormAtivo] = useState<Partial<Ativo>>({ ativo: 1 });
  const [formPlano, setFormPlano] = useState<Partial<Plano>>({ intervalo_dias: 90, ativo: 1 });

  const podeAcessar =
    temPermissao(permissoes, "preventivas", "acessar_modulo") ||
    loginGrupoNome === "ADMINISTRADOR";
  const podeTipos =
    temPermissao(permissoes, "preventivas", "gerenciar_tipos") || loginGrupoNome === "ADMINISTRADOR";
  const podeAtivos =
    temPermissao(permissoes, "preventivas", "gerenciar_ativos") || loginGrupoNome === "ADMINISTRADOR";
  const podePlanos =
    temPermissao(permissoes, "preventivas", "gerenciar_planos") || loginGrupoNome === "ADMINISTRADOR";
  const podeGerarOs =
    temPermissao(permissoes, "preventivas", "gerar_os") || loginGrupoNome === "ADMINISTRADOR";
  const [gerandoPlanoId, setGerandoPlanoId] = useState<number | null>(null);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [erroAgenda, setErroAgenda] = useState<string | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);

  function showToast(message: string, type: "error" | "success" = "error") {
    setToast({ open: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 2800);
  }

  const api = useCallback(
    async (url: string, options?: RequestInit) => {
      const method = (options?.method || "GET").toUpperCase();
      const headers = { "Content-Type": "application/json", ...(options?.headers || {}) };
      let finalUrl = url;
      let body = options?.body;

      if (method === "GET") {
        const sep = url.includes("?") ? "&" : "?";
        finalUrl = `${url}${sep}unidade=${encodeURIComponent(unidade)}`;
      } else {
        const parsed = body ? JSON.parse(String(body)) : {};
        body = JSON.stringify({ ...parsed, unidade });
      }

      let res: Response;
      try {
        res = await fetch(finalUrl, { ...options, method, headers, body });
      } catch {
        throw new Error("Backend indisponível. Confirme se o serviço Node (porta 4000) está rodando.");
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.error ||
            (res.status === 500
              ? "Erro no servidor (500). Tente Atualizar; se persistir, reinicie o backend."
              : `Erro HTTP ${res.status}`)
        );
      }
      return data;
    },
    [unidade]
  );

  const carregar = useCallback(async () => {
    if (!unidade || !podeAcessar) return;
    setLoading(true);
    setErroAgenda(null);
    setErroLista(null);
    try {
      if (secao === "tipos") {
        const data = await api(ENDPOINTS.PREVENTIVAS_TIPOS);
        setTipos(Array.isArray(data) ? data : []);
      }
      if (secao === "ativos") {
        const [a, t, dRes] = await Promise.all([
          api(ENDPOINTS.PREVENTIVAS_ATIVOS),
          api(ENDPOINTS.PREVENTIVAS_TIPOS),
          fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ unidade }),
          }).then((r) => r.json()),
        ]);
        setAtivos(Array.isArray(a) ? a : []);
        setTipos(Array.isArray(t) ? t : []);
        setDepartamentos(ordenarPorTexto(Array.isArray(dRes) ? dRes : [], "descricao"));
      }
      if (secao === "planos") {
        const [p, a] = await Promise.all([
          api(ENDPOINTS.PREVENTIVAS_PLANOS),
          api(ENDPOINTS.PREVENTIVAS_ATIVOS),
        ]);
        setPlanos(Array.isArray(p) ? p : []);
        setAtivos(Array.isArray(a) ? a : []);
      }
      if (secao === "agenda") {
        const data = await api(ENDPOINTS.PREVENTIVAS_AGENDA);
        setAgenda(
          data || { atrasadas: [], a_vencer: [], em_dia: [], sem_data: [], total: 0 }
        );
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao carregar dados";
      if (secao === "agenda") {
        setErroAgenda(msg);
        setAgenda(null);
      } else {
        setErroLista(msg);
      }
      showToast(msg);
    } finally {
      setLoading(false);
    }
  }, [api, unidade, podeAcessar, secao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (!podeAcessar) {
    return (
      <div className="p-8 text-center text-red-600 font-bold text-xl">
        Você não tem permissão para acessar este módulo.
      </div>
    );
  }

  async function salvarTipo() {
    if (!formTipo.descricao.trim()) {
      showToast("Preencha todos os campos obrigatórios!");
      return;
    }
    setLoading(true);
    try {
      if (formTipo.id) {
        await api(`${ENDPOINTS.PREVENTIVAS_TIPOS}/${formTipo.id}`, {
          method: "PUT",
          body: JSON.stringify({ descricao: formTipo.descricao, ativo: formTipo.ativo }),
        });
      } else {
        await api(ENDPOINTS.PREVENTIVAS_TIPOS, {
          method: "POST",
          body: JSON.stringify({ descricao: formTipo.descricao, ativo: formTipo.ativo }),
        });
      }
      setModalTipo(false);
      setFormTipo({ descricao: "", ativo: 1 });
      showToast(formTipo.id ? "Tipo atualizado" : "Tipo criado", "success");
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  }

  async function salvarAtivo() {
    if (!formAtivo.tipo_id || !formAtivo.descricao?.trim()) {
      showToast("Preencha todos os campos obrigatórios!");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        tipo_id: Number(formAtivo.tipo_id),
        departamento_id: formAtivo.departamento_id || null,
        codigo: formAtivo.codigo || null,
        descricao: formAtivo.descricao,
        local_descricao: formAtivo.local_descricao || null,
        marca: formAtivo.marca || null,
        modelo: formAtivo.modelo || null,
        observacoes: formAtivo.observacoes || null,
        ativo: formAtivo.ativo === 0 ? 0 : 1,
      };
      if (formAtivo.id) {
        await api(`${ENDPOINTS.PREVENTIVAS_ATIVOS}/${formAtivo.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await api(ENDPOINTS.PREVENTIVAS_ATIVOS, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setModalAtivo(false);
      setFormAtivo({ ativo: 1 });
      showToast(formAtivo.id ? "Ativo atualizado" : "Ativo criado", "success");
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  }

  async function salvarPlano() {
    if (!formPlano.ativo_id || !formPlano.descricao?.trim()) {
      showToast("Preencha todos os campos obrigatórios!");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ativo_id: Number(formPlano.ativo_id),
        descricao: formPlano.descricao,
        intervalo_dias: Number(formPlano.intervalo_dias || 90),
        dth_ultima: formPlano.dth_ultima || null,
        dth_proxima: formPlano.dth_proxima || null,
        ativo: formPlano.ativo === 0 ? 0 : 1,
      };
      if (formPlano.id) {
        await api(`${ENDPOINTS.PREVENTIVAS_PLANOS}/${formPlano.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await api(ENDPOINTS.PREVENTIVAS_PLANOS, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setModalPlano(false);
      setFormPlano({ intervalo_dias: 90, ativo: 1 });
      showToast(formPlano.id ? "Plano atualizado" : "Plano criado", "success");
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  }

  function calcularProximaData() {
    const dias = Number(formPlano.intervalo_dias);
    if (!Number.isFinite(dias) || dias < 1) {
      showToast("Informe um intervalo válido (dias)");
      return;
    }
    const dt = new Date();
    dt.setDate(dt.getDate() + dias);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    const dth_proxima = `${yyyy}-${mm}-${dd} ${hh}:${mi}:00`;
    setFormPlano((f) => ({ ...f, dth_proxima }));
  }

  async function confirmarExclusao() {
    if (!confirmaExcluir) return;
    setLoading(true);
    try {
      const map = {
        tipo: ENDPOINTS.PREVENTIVAS_TIPOS,
        ativo: ENDPOINTS.PREVENTIVAS_ATIVOS,
        plano: ENDPOINTS.PREVENTIVAS_PLANOS,
      };
      await api(`${map[confirmaExcluir.tipo]}/${confirmaExcluir.id}`, {
        method: "DELETE",
        body: "{}",
      });
      showToast("Registro excluído", "success");
      setConfirmaExcluir(null);
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao excluir");
    } finally {
      setLoading(false);
    }
  }

  async function gerarOs(planoId: number) {
    setGerandoPlanoId(planoId);
    setLoading(true);
    try {
      const data = await api(ENDPOINTS.PREVENTIVAS_GERAR_OS, {
        method: "POST",
        body: JSON.stringify({
          plano_id: planoId,
          usuario_id: loginLoginId ? Number(loginLoginId) : null,
          solicitante_nome: loginLoginNome || "PREVENTIVAS",
        }),
      });
      showToast(`OS #${data.os_id} gerada com sucesso`, "success");
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao gerar OS");
    } finally {
      setGerandoPlanoId(null);
      setLoading(false);
    }
  }

  async function gerarTodasAtrasadas() {
    if (!confirm("Gerar OS para todas as preventivas atrasadas?")) return;
    setLoading(true);
    try {
      const data = await api(ENDPOINTS.PREVENTIVAS_GERAR_OS_ATRASADAS, {
        method: "POST",
        body: JSON.stringify({
          usuario_id: loginLoginId ? Number(loginLoginId) : null,
          solicitante_nome: loginLoginNome || "PREVENTIVAS",
        }),
      });
      const qtd = data.geradas || 0;
      const erros = Array.isArray(data.erros) ? data.erros.length : 0;
      showToast(
        qtd > 0
          ? `${qtd} OS gerada(s)${erros ? ` (${erros} ignorada(s))` : ""}`
          : erros
            ? `Nenhuma OS nova (${erros} já tinham OS aberta)`
            : "Nenhuma atrasada para gerar",
        qtd > 0 ? "success" : "error"
      );
      await carregar();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Erro ao gerar OS");
    } finally {
      setLoading(false);
    }
  }

  function linhasAgendaPdf(itens: Plano[]) {
    if (!itens.length) return [["—", "Nenhum item", "", "", "", "", ""]];
    return itens.map((p) => [
      p.ativo_descricao || "-",
      p.tipo_descricao || "-",
      p.descricao || "-",
      p.local_descricao || "-",
      p.dth_proxima || "-",
      labelSituacao(p.situacao),
      p.os_aberta_id ? `OS #${p.os_aberta_id}` : "-",
    ]);
  }

  function gerarPdfAgenda() {
    if (!agenda) {
      showToast("Carregue a agenda antes de gerar o relatório");
      return;
    }
    setGerandoPDF(true);
    setTimeout(() => {
      try {
        // Paisagem A4: cabe melhor a grade da agenda
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const dataAtual = new Date();
        const dataStr = dataAtual.toLocaleDateString("pt-BR");
        const horaStr = dataAtual.toLocaleTimeString("pt-BR");
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(`ADVSERV - ${unidade.toUpperCase()}`, 14, 14);
        doc.setFontSize(12);
        doc.text("AGENDA DE PREVENTIVAS", 14, 21);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`${dataStr}  ${horaStr}`, pageWidth - 14, 14, { align: "right" });
        doc.text(`Total de planos: ${agenda.total}`, pageWidth - 14, 21, { align: "right" });

        const secoes: { titulo: string; itens: Plano[]; cor: [number, number, number] }[] = [
          { titulo: `Atrasadas (${agenda.atrasadas.length})`, itens: agenda.atrasadas, cor: [220, 38, 38] },
          { titulo: `A vencer — 15 dias (${agenda.a_vencer.length})`, itens: agenda.a_vencer, cor: [202, 138, 4] },
          { titulo: `Em dia (${agenda.em_dia.length})`, itens: agenda.em_dia, cor: [22, 163, 74] },
          { titulo: `Sem data (${agenda.sem_data.length})`, itens: agenda.sem_data, cor: [107, 114, 128] },
        ];

        let startY = 28;
        for (const secaoPdf of secoes) {
          if (startY > 180) {
            doc.addPage();
            startY = 16;
          }

          doc.setFillColor(...secaoPdf.cor);
          doc.rect(14, startY, pageWidth - 28, 7, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(secaoPdf.titulo, 16, startY + 5);
          doc.setTextColor(0, 0, 0);
          startY += 8;

          autoTable(doc, {
            startY,
            head: [["Ativo", "Tipo", "Plano", "Local", "Próxima", "Situação", "OS"]],
            body: linhasAgendaPdf(secaoPdf.itens),
            styles: { fontSize: 8, cellPadding: 1.5, valign: "middle", overflow: "linebreak" },
            headStyles: {
              fillColor: [31, 41, 55],
              textColor: [255, 255, 255],
              fontStyle: "bold",
              fontSize: 8,
            },
            columnStyles: {
              0: { cellWidth: 40 },
              1: { cellWidth: 35 },
              2: { cellWidth: 50 },
              3: { cellWidth: 35 },
              4: { cellWidth: 40 },
              5: { cellWidth: 25 },
              6: { cellWidth: 22 },
            },
            margin: { left: 14, right: 14 },
            tableWidth: "auto",
            pageBreak: "auto",
          });

          const last = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable;
          startY = (last?.finalY ?? startY) + 6;
        }

        const nome = `Agenda_Preventivas_${unidade}_${dataStr.replace(/\//g, "-")}_${horaStr.replace(/:/g, "-")}.pdf`;
        doc.save(nome);
        showToast("Relatório PDF gerado", "success");
      } catch (e) {
        console.error(e);
        showToast(e instanceof Error ? e.message : "Erro ao gerar PDF");
      } finally {
        setGerandoPDF(false);
      }
    }, 50);
  }

  function renderListaAgenda(
    titulo: string,
    itens: Plano[],
    cor: string,
    permitirGerar: boolean
  ) {
    return (
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className={`inline-block px-3 py-1 rounded font-bold text-sm ${cor}`}>
            {titulo} ({itens.length})
          </div>
          {permitirGerar && podeGerarOs && titulo.startsWith("Atrasadas") && itens.length > 0 && (
            <button
              type="button"
              onClick={gerarTodasAtrasadas}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-semibold cursor-pointer"
            >
              <i className="fa fa-file-circle-plus mr-1"></i>
              Gerar OS das atrasadas
            </button>
          )}
        </div>
        {itens.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhum item.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg shadow bg-white">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-700">
                  <th className="p-2">Ativo</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Plano</th>
                  <th className="p-2">Local</th>
                  <th className="p-2">Próxima</th>
                  <th className="p-2">Situação</th>
                  <th className="p-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((p, idx) => (
                  <tr key={p.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="p-2 text-black">{p.ativo_descricao}</td>
                    <td className="p-2 text-black">{p.tipo_descricao}</td>
                    <td className="p-2 text-black">{p.descricao}</td>
                    <td className="p-2 text-black">{p.local_descricao || "-"}</td>
                    <td className="p-2 text-black">{p.dth_proxima || "-"}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${badgeSituacao(p.situacao)}`}>
                        {labelSituacao(p.situacao)}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {p.os_aberta_id ? (
                        <span className="text-xs font-semibold text-blue-700">
                          OS #{p.os_aberta_id}
                        </span>
                      ) : permitirGerar && podeGerarOs ? (
                        <button
                          type="button"
                          disabled={gerandoPlanoId === p.id}
                          onClick={() => gerarOs(p.id)}
                          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-3 py-1 rounded text-xs font-semibold cursor-pointer"
                          title="Gerar ordem de serviço"
                        >
                          <i className="fa fa-file-circle-plus mr-1"></i>
                          Gerar OS
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full p-6 relative">
      {loading && visivel && <Loader />}
      {gerandoPDF && visivel && <Loader />}
      {toast.open && visivel && (
        <div
          className={`fixed top-6 left-1/2 z-[9999] -translate-x-1/2 px-6 py-3 rounded shadow-lg text-white font-semibold ${
            toast.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{titulos[secao]}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Unidade <b>{unidade.toUpperCase()}</b>
          </p>
        </div>
        <div className="flex gap-2">
          {secao === "agenda" && (
            <button
              type="button"
              onClick={gerarPdfAgenda}
              disabled={!agenda || gerandoPDF}
              className="bg-gray-800 hover:bg-black disabled:opacity-60 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              title="Gerar relatório PDF A4"
            >
              <i className="fa fa-print mr-2"></i>Imprimir / PDF
            </button>
          )}
          <button
            type="button"
            onClick={carregar}
            className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded font-semibold cursor-pointer"
          >
            <i className="fa fa-rotate mr-2"></i>Atualizar
          </button>
          {secao === "tipos" && podeTipos && (
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              onClick={() => {
                setFormTipo({ descricao: "", ativo: 1 });
                setModalTipo(true);
              }}
            >
              <i className="fa fa-plus mr-2"></i>Novo
            </button>
          )}
          {secao === "ativos" && podeAtivos && (
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              onClick={() => {
                setFormAtivo({ ativo: 1 });
                setModalAtivo(true);
              }}
            >
              <i className="fa fa-plus mr-2"></i>Novo
            </button>
          )}
          {secao === "planos" && podePlanos && (
            <button
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              onClick={() => {
                setFormPlano({ intervalo_dias: 90, ativo: 1 });
                setModalPlano(true);
              }}
            >
              <i className="fa fa-plus mr-2"></i>Novo
            </button>
          )}
        </div>
      </div>

      {secao === "agenda" && (
        <div>
          {loading && !agenda ? (
            <p className="text-gray-400">Carregando...</p>
          ) : erroAgenda && !agenda ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-red-600 font-semibold mb-3">Não foi possível carregar a agenda.</p>
              <p className="text-gray-500 text-sm mb-4">{erroAgenda}</p>
              <button
                type="button"
                onClick={carregar}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              >
                Tentar novamente
              </button>
            </div>
          ) : !agenda || agenda.total === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Nenhum plano ativo. Cadastre ativos e planos para ver a agenda.
            </div>
          ) : (
            <>
              {renderListaAgenda("Atrasadas", agenda.atrasadas, "bg-red-600 text-white", true)}
              {renderListaAgenda("A vencer (15 dias)", agenda.a_vencer, "bg-yellow-400 text-gray-900", true)}
              {renderListaAgenda("Em dia", agenda.em_dia, "bg-green-500 text-white", true)}
              {renderListaAgenda("Sem data", agenda.sem_data, "bg-gray-300 text-gray-800", false)}
            </>
          )}
        </div>
      )}

      {secao === "tipos" && (
        <div className="overflow-x-auto rounded-lg shadow bg-white">
          {erroLista ? (
            <div className="p-6 text-center">
              <p className="text-red-600 font-semibold mb-3">{erroLista}</p>
              <button
                type="button"
                onClick={carregar}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold cursor-pointer"
              >
                Tentar novamente
              </button>
            </div>
          ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 text-black">ID</th>
                <th className="p-2 text-black">Descrição</th>
                <th className="p-2 text-black">Status</th>
                <th className="p-2 text-center text-black">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tipos.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-gray-400 py-8">
                    Nenhum tipo cadastrado.
                  </td>
                </tr>
              ) : (
                tipos.map((t, idx) => (
                  <tr key={t.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="p-2 text-black">{t.id}</td>
                    <td className="p-2 text-black">{t.descricao}</td>
                    <td className="p-2 text-black">{t.ativo ? "Ativo" : "Inativo"}</td>
                    <td className="p-2 flex gap-2 justify-center">
                      {podeTipos && (
                        <>
                          <button
                            className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 cursor-pointer"
                            title="Editar"
                            onClick={() => {
                              setFormTipo({ id: t.id, descricao: t.descricao, ativo: t.ativo });
                              setModalTipo(true);
                            }}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 cursor-pointer"
                            title="Excluir"
                            onClick={() =>
                              setConfirmaExcluir({ tipo: "tipo", id: t.id, label: t.descricao })
                            }
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          )}
        </div>
      )}

      {secao === "ativos" && (
        <div className="overflow-x-auto rounded-lg shadow bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 text-black">ID</th>
                <th className="p-2 text-black">Descrição</th>
                <th className="p-2 text-black">Tipo</th>
                <th className="p-2 text-black">Local</th>
                <th className="p-2 text-black">Departamento</th>
                <th className="p-2 text-center text-black">Ações</th>
              </tr>
            </thead>
            <tbody>
              {ativos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-8">
                    Nenhum ativo cadastrado.
                  </td>
                </tr>
              ) : (
                ativos.map((a, idx) => (
                  <tr key={a.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="p-2 text-black">{a.id}</td>
                    <td className="p-2 text-black">{a.descricao}</td>
                    <td className="p-2 text-black">{a.tipo_descricao}</td>
                    <td className="p-2 text-black">{a.local_descricao || "-"}</td>
                    <td className="p-2 text-black">{a.departamento_descricao || "-"}</td>
                    <td className="p-2 flex gap-2 justify-center">
                      {podeAtivos && (
                        <>
                          <button
                            className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 cursor-pointer"
                            title="Editar"
                            onClick={() => {
                              setFormAtivo(a);
                              setModalAtivo(true);
                            }}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 cursor-pointer"
                            title="Excluir"
                            onClick={() =>
                              setConfirmaExcluir({ tipo: "ativo", id: a.id, label: a.descricao })
                            }
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {secao === "planos" && (
        <div className="overflow-x-auto rounded-lg shadow bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="p-2 text-black">Ativo</th>
                <th className="p-2 text-black">Plano</th>
                <th className="p-2 text-black">Intervalo</th>
                <th className="p-2 text-black">Próxima</th>
                <th className="p-2 text-black">Situação</th>
                <th className="p-2 text-center text-black">Ações</th>
              </tr>
            </thead>
            <tbody>
              {planos.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-8">
                    Nenhum plano cadastrado.
                  </td>
                </tr>
              ) : (
                planos.map((p, idx) => (
                  <tr key={p.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="p-2 text-black">{p.ativo_descricao}</td>
                    <td className="p-2 text-black">{p.descricao}</td>
                    <td className="p-2 text-black">{p.intervalo_dias}d</td>
                    <td className="p-2 text-black">{p.dth_proxima || "-"}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${badgeSituacao(p.situacao)}`}>
                        {labelSituacao(p.situacao)}
                      </span>
                    </td>
                    <td className="p-2 flex gap-2 justify-center">
                      {podePlanos && (
                        <>
                          <button
                            className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 cursor-pointer"
                            title="Editar"
                            onClick={() => {
                              setFormPlano(p);
                              setModalPlano(true);
                            }}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 cursor-pointer"
                            title="Excluir"
                            onClick={() =>
                              setConfirmaExcluir({ tipo: "plano", id: p.id, label: p.descricao })
                            }
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Tipo */}
      {modalTipo && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-[95vw] sm:max-w-[420px] w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900">
              {formTipo.id ? "Editar Tipo" : "Novo Tipo"}
            </h3>
            <div className="flex flex-col gap-3">
              {formTipo.id && (
                <>
                  <label className="text-sm font-semibold text-gray-700">ID</label>
                  <input
                    className="w-full rounded px-2 py-2 border border-gray-300 bg-gray-100 text-gray-900"
                    value={formTipo.id}
                    readOnly
                  />
                </>
              )}
              <label className="text-sm font-semibold text-gray-700">
                Descrição <span className="text-red-500">*</span>
              </label>
              <input
                autoFocus
                className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                value={formTipo.descricao}
                onChange={(e) => setFormTipo((f) => ({ ...f, descricao: e.target.value }))}
                placeholder="Ex.: Ar-condicionado"
              />
              <label className="flex items-center gap-2 text-sm text-black">
                <input
                  type="checkbox"
                  checked={formTipo.ativo === 1}
                  onChange={(e) => setFormTipo((f) => ({ ...f, ativo: e.target.checked ? 1 : 0 }))}
                />
                Ativo
              </label>
            </div>
            <div className="flex gap-4 mt-6 justify-center">
              <button
                onClick={salvarTipo}
                className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700 cursor-pointer"
              >
                Salvar
              </button>
              <button
                onClick={() => setModalTipo(false)}
                className="bg-red-500 text-white px-6 py-2 rounded font-semibold hover:bg-red-600 cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ativo */}
      {modalAtivo && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-[95vw] sm:max-w-[560px] w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-gray-900">
              {formAtivo.id ? "Editar Ativo" : "Novo Ativo"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold text-gray-700">
                  Tipo <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.tipo_id || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, tipo_id: Number(e.target.value) }))}
                >
                  <option value="">Selecione...</option>
                  {tipos.filter((t) => t.ativo).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold text-gray-700">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.descricao || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, descricao: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Código / patrimônio</label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.codigo || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, codigo: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Departamento</label>
                <select
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.departamento_id || ""}
                  onChange={(e) =>
                    setFormAtivo((f) => ({
                      ...f,
                      departamento_id: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                >
                  <option value="">—</option>
                  {departamentos.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.descricao}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Local</label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.local_descricao || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, local_descricao: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Marca</label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.marca || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, marca: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Modelo</label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.modelo || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, modelo: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Observações</label>
                <input
                  className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={formAtivo.observacoes || ""}
                  onChange={(e) => setFormAtivo((f) => ({ ...f, observacoes: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6 justify-center">
              <button
                onClick={salvarAtivo}
                className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700 cursor-pointer"
              >
                Salvar
              </button>
              <button
                onClick={() => setModalAtivo(false)}
                className="bg-red-500 text-white px-6 py-2 rounded font-semibold hover:bg-red-600 cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Plano */}
      {modalPlano && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-[95vw] sm:max-w-[480px] w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-900">
              {formPlano.id ? "Editar Plano" : "Novo Plano"}
            </h3>
            <div className="flex flex-col gap-3">
              <label className="text-sm font-semibold text-gray-700">
                Ativo <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                value={formPlano.ativo_id || ""}
                onChange={(e) => setFormPlano((f) => ({ ...f, ativo_id: Number(e.target.value) }))}
              >
                <option value="">Selecione...</option>
                {ativos.filter((a) => a.ativo).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.descricao}
                  </option>
                ))}
              </select>
              <label className="text-sm font-semibold text-gray-700">
                Descrição <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                value={formPlano.descricao || ""}
                onChange={(e) => setFormPlano((f) => ({ ...f, descricao: e.target.value }))}
                placeholder="Ex.: Limpeza de filtros"
              />
              <label className="text-sm font-semibold text-gray-700">Intervalo (dias)</label>
              <input
                type="number"
                min={1}
                className="w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                value={formPlano.intervalo_dias || 90}
                onChange={(e) =>
                  setFormPlano((f) => ({ ...f, intervalo_dias: Number(e.target.value) }))
                }
              />
              <label className="text-sm font-semibold text-gray-700">Próxima data</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="datetime-local"
                  className="flex-1 w-full rounded px-2 py-2 border border-gray-300 text-gray-900"
                  value={
                    formPlano.dth_proxima
                      ? String(formPlano.dth_proxima).replace(" ", "T").slice(0, 16)
                      : ""
                  }
                  onChange={(e) =>
                    setFormPlano((f) => ({
                      ...f,
                      dth_proxima: e.target.value ? e.target.value.replace("T", " ") + ":00" : null,
                    }))
                  }
                />
                <button
                  type="button"
                  onClick={calcularProximaData}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold whitespace-nowrap cursor-pointer"
                  title="Data atual + intervalo (dias)"
                >
                  Calcular data
                </button>
              </div>
            </div>
            <div className="flex gap-4 mt-6 justify-center">
              <button
                onClick={salvarPlano}
                className="bg-green-600 text-white px-6 py-2 rounded font-semibold hover:bg-green-700 cursor-pointer"
              >
                Salvar
              </button>
              <button
                onClick={() => setModalPlano(false)}
                className="bg-red-500 text-white px-6 py-2 rounded font-semibold hover:bg-red-600 cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirma exclusão */}
      {confirmaExcluir && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 max-w-[400px] w-full text-center">
            <h3 className="text-xl font-bold mb-2 text-gray-900">Confirma exclusão?</h3>
            <p className="text-gray-600 mb-6">
              Deseja excluir <b>{confirmaExcluir.label}</b>?
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={confirmarExclusao}
                className="bg-red-600 text-white px-6 py-2 rounded font-semibold hover:bg-red-700 cursor-pointer"
              >
                Excluir
              </button>
              <button
                onClick={() => setConfirmaExcluir(null)}
                className="bg-gray-500 text-white px-6 py-2 rounded font-semibold hover:bg-gray-600 cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
